import groupBy from "lodash.groupby";
import sortBy from "lodash.sortby";
import startCase from "lodash.startcase";
import { CircleHelp } from "lucide-react";
import React, { FC, useEffect } from "react";
import { Updater, useImmer } from "use-immer";

import {
  IgButton,
  IgDivider,
  IgIconButton,
  IgPaper,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { ConfigurationDto } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { usePatchConfigurations } from "@src/api/configurations/patch-configurations";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { ConfigurationInput } from "@src/domains/Settings/AdminSettings/AdvancedSettings/ConfigurationInputs";
import {
  getNewModifiedConfigs,
  onConfigChange,
  onLockedChange,
} from "@src/domains/Settings/Utils/Utils";

export const AnalyticsSettings = () => {
  const [settingsState, setSettingsState] = useImmer<ConfigurationDto[]>([]);

  const theme = useTheme();
  const mutateConfigurations = usePatchConfigurations();
  const organization = useGetOrg();

  useEffect(() => {
    setSettingsState(
      organization.data.org.configurations.filter(
        (config) => config.topic === "ANALYTICS"
      )
    );
  }, [organization.data.org.configurations]);

  return (
    <Grid item>
      <IgPaper>
        <Grid item container direction={"column"} rowSpacing={3}>
          <Grid item>
            <IgTypography variant={"h3"} fontWeight={600}>
              Analytics Settings
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography
              variant={"body1"}
              color={theme.palette.text.secondary}
            >
              Set defaults for analytics settings that will affect how your test
              and personalization analytics are returned
            </IgTypography>
          </Grid>

          <Grid container direction={"column"} rowSpacing={2}>
            {sortBy(
              Object.entries(
                groupBy(
                  settingsState.filter(
                    (config) => config.topic === "ANALYTICS"
                  ),
                  (_) => _.topic
                )
              ),
              (_) => _[0]
            ).map(([key, settings], index) => {
              return (
                <Grid item key={`${key}-${index}`}>
                  <Grid container direction={"column"} rowSpacing={2}>
                    {Object.entries(groupBy(settings, (_) => _.type)).map(
                      ([key, settings], groupIndex) => {
                        return (
                          <Grid
                            container
                            direction={"column"}
                            rowSpacing={2}
                            key={`${key}-${groupIndex}`}
                          >
                            <Grid item>
                              <Grid item textAlign={"end"}>
                                <IgTypography
                                  sx={{ fontWeight: 600 }}
                                >{`${startCase(key)} Fields`}</IgTypography>
                              </Grid>
                              <Grid
                                container
                                direction={"column"}
                                rowSpacing={
                                  settings.length && settings[0].type === "Json"
                                    ? 3
                                    : 1
                                }
                              >
                                {sortBy(settings, "key").map((setting) => {
                                  return (
                                    <Grid item key={setting.key}>
                                      <ConfigurationInput
                                        config={setting}
                                        onLockedChange={
                                          setting.topic === "WEB_UI_SCOPE"
                                            ? (locked) => {
                                                onLockedChange(
                                                  setting.key,
                                                  locked,
                                                  setSettingsState
                                                );
                                              }
                                            : undefined
                                        }
                                        onChange={(newValue: any) =>
                                          onConfigChange(
                                            setting.key,
                                            newValue,
                                            setSettingsState
                                          )
                                        }
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Grid>
                            <Grid item>
                              <IgDivider spacing={"m"} />
                            </Grid>
                          </Grid>
                        );
                      }
                    )}
                    <Grid item xs textAlign={"end"}>
                      <IgButton
                        loading={mutateConfigurations.isPending}
                        style={{ marginTop: 5 }}
                        size="medium"
                        variant={"contained"}
                        onClick={() =>
                          mutateConfigurations.mutate(
                            getNewModifiedConfigs(
                              settingsState,
                              organization.data.org.configurations
                            ) || []
                          )
                        }
                      >
                        Save Configurations
                      </IgButton>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </IgPaper>
    </Grid>
  );
};
